/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Chip, ChipGroup, Dialog, InlineNotification, TextField, Typography2 } from '@coursera/cds-core';
import { AIGenerateIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import useDefineGoalSuggestions from 'bundles/enterprise-admin-messaging-v2/hooks/useDefineGoalSuggestions';
import { useMessagingTrackingData } from 'bundles/enterprise-admin-messaging-v2/hooks/useMessagingTrackingData';
import type { AIMessageActionKey } from 'bundles/enterprise-admin-messaging-v2/utils/aiMessageActions';
import { AI_MESSAGE_ACTIONS } from 'bundles/enterprise-admin-messaging-v2/utils/aiMessageActions';
import {
  MESSAGING_BANNER,
  MESSAGING_BUTTON,
  MESSAGING_MODAL,
  MESSAGING_TEXT_INPUT,
  SUGGESTION_ID_BY_ACTION_KEY,
} from 'bundles/enterprise-admin-messaging-v2/utils/eventing';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  prompt: string;
  onPromptChange: (value: string) => void;
  onGenerate: () => void;
  onCancel: () => void;
  /**
   * Hook for the "Draft a message manually" link. Stubbed as a no-op until
   * the manual-draft flow ships in a follow-up PR.
   */
  onDraftManually?: () => void;
  /** When true, render the single generic "couldn't generate" error banner. */
  showError?: boolean;
  /** Dismiss handler for the error banner. */
  onDismissError?: () => void;
  /**
   * Pre-selected audience label (table-selection entry points). When set, a
   * removable pill is shown; clearing it drops the audience and reverts to the
   * generic AI flow.
   */
  audienceLabel?: string;
  /** Clears the pre-selected audience (the pill's ✕). */
  onRemoveAudience?: () => void;
};

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  // Persistent live region wrapper. Kept outside `body` so the always-mounted
  // empty element doesn't introduce a stray flex gap; the margin only applies
  // once the banner is actually rendered inside it.
  errorRegion: css`
    &:not(:empty) {
      margin-bottom: var(--cds-spacing-200);
    }
  `,
  suggestionRow: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--cds-spacing-100);
  `,
  suggestion: css`
    /* Per Figma: width=hug capped at 443px, height=hug (36px on a single line),
       2px gradient border (blue-800 → blue-600), 8px radius, 8/16 padding,
       white bg. The double-background trick renders the gradient border
       cleanly with rounded corners. */
    align-items: center;
    background: linear-gradient(
          var(--cds-color-neutral-background-primary),
          var(--cds-color-neutral-background-primary)
        )
        padding-box,
      linear-gradient(180deg, var(--cds-color-blue-800), var(--cds-color-blue-700)) border-box;
    border: 2px solid transparent;
    border-radius: var(--cds-border-radius-100);
    color: var(--cds-color-interactive-secondary);
    cursor: pointer;
    padding: var(--cds-spacing-100) var(--cds-spacing-200);
    text-align: left;

    &:hover:not(:disabled) {
      background: linear-gradient(
            var(--cds-color-interactive-background-primary-hover-weak),
            var(--cds-color-interactive-background-primary-hover-weak)
          )
          padding-box,
        linear-gradient(180deg, var(--cds-color-blue-800), var(--cds-color-blue-700)) border-box;
    }

    &:focus-visible {
      outline: 2px solid var(--cds-color-interactive-stroke-primary-focus);
      outline-offset: 2px;
    }
  `,
  actions: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-150);
    width: 100%;
  `,
  manualLink: css`
    margin-left: auto;
  `,
  // Row 7: the Generate button uses the Coursera "coach" primary gradient
  // (Figma primary-gradient blue-800 → blue-600). CDS has no token for this
  // gradient, so the exact stops are inlined. Gated to :not(:disabled) so the
  // disabled (empty-prompt) state keeps its default greyed-out treatment.
  generateButton: css`
    &&:not(:disabled) {
      /* stylelint-disable-next-line color-no-hex -- no CDS token exists for the coach primary gradient */
      background-image: linear-gradient(-69.55deg, #0048b0 5.78%, #1e72eb 83.49%);
      border-color: transparent;
    }
  `,
};

/**
 * Step 1 of the AI Message modal: the admin types (or picks) a natural-
 * language prompt describing the audience + goal. Pressing Generate hands
 * the prompt off to the orchestrator, which transitions to the Generating
 * loading state.
 */
const DefineGoalScreen = ({
  prompt,
  onPromptChange,
  onGenerate,
  onCancel,
  onDraftManually,
  showError = false,
  onDismissError,
  audienceLabel,
  onRemoveAudience,
}: Props) => {
  const trimmedPrompt = prompt.trim();
  const canGenerate = trimmedPrompt.length > 0;
  const { suggestions, refresh: refreshSuggestions } = useDefineGoalSuggestions();

  const track = useTracker();
  const trackingData = useMessagingTrackingData();

  // `suggestions` is frozen for the modal session (see useDefineGoalSuggestions),
  // so memoizing keeps `suggestionIds` referentially stable — the view_suggestion
  // effect can depend on it directly and fires once per open, not per re-render.
  const suggestionIds = React.useMemo(() => suggestions.map((key) => SUGGESTION_ID_BY_ACTION_KEY[key]), [suggestions]);

  // Modal-step impression.
  React.useEffect(() => {
    track('view_modal', { ...trackingData, modal: { name: MESSAGING_MODAL.CREATE_STEP1 } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Suggestion-chips impression.
  React.useEffect(() => {
    track('view_suggestion', {
      suggestionIds,
      ...trackingData,
      modal: { name: MESSAGING_MODAL.CREATE_STEP1 },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestionIds]);

  // Banner impression: fires when the AI-generation error becomes visible.
  React.useEffect(() => {
    if (showError) {
      track('view_banner', {
        bannerName: MESSAGING_BANNER.AI_GENERATION_FAILED,
        ...trackingData,
        modal: { name: MESSAGING_MODAL.CREATE_STEP1 },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showError]);

  // Rotate the suggestion set in localStorage for the NEXT modal open. The
  // current chips stay stable for the rest of this session.
  const handleGenerate = React.useCallback<() => void>(() => {
    track('click_button', {
      button: { name: MESSAGING_BUTTON.GENERATE },
      ...trackingData,
      modal: { name: MESSAGING_MODAL.CREATE_STEP1 },
    });
    refreshSuggestions();
    onGenerate();
  }, [track, trackingData, refreshSuggestions, onGenerate]);

  // Skip the first focus event (the autoFocus when modal opens is not a user
  // interaction), then track all subsequent focuses as text input clicks.
  const isAutoFocusOnMount = React.useRef(true);

  /** Fire the text-input focus event for the AI prompt textarea (skips the on-open autofocus). */
  const handlePromptFocus = React.useCallback<() => void>(() => {
    if (isAutoFocusOnMount.current) {
      isAutoFocusOnMount.current = false;
      return;
    }
    track('click_text_input', {
      textInput: { name: MESSAGING_TEXT_INPUT.AI_PROMPT },
      ...trackingData,
      modal: { name: MESSAGING_MODAL.CREATE_STEP1 },
    });
  }, [track, trackingData]);

  /**
   * Track a suggestion-chip click, then apply the chip's prompt.
   * @param key - The action key backing the clicked chip.
   * @param index - 0-based position of the chip among those rendered.
   */
  const handleSuggestionClick = React.useCallback<(key: AIMessageActionKey, index: number) => void>(
    (key, index) => {
      track('interact_suggestion', {
        suggestion: {
          id: SUGGESTION_ID_BY_ACTION_KEY[key],
          text: AI_MESSAGE_ACTIONS[key].prompt,
          index,
        },
        interactionType: 'click',
        ...trackingData,
        modal: { name: MESSAGING_MODAL.CREATE_STEP1 },
      });
      onPromptChange(AI_MESSAGE_ACTIONS[key].prompt);
    },
    [track, trackingData, onPromptChange]
  );

  /** Track the "Draft a message manually" click, then run the existing handler. */
  const handleDraftManually = React.useCallback<() => void>(() => {
    track('click_button', {
      button: { name: MESSAGING_BUTTON.CREATE_MANUALLY },
      ...trackingData,
      modal: { name: MESSAGING_MODAL.CREATE_STEP1 },
    });
    onDraftManually?.();
  }, [track, trackingData, onDraftManually]);

  return (
    <React.Fragment>
      <Dialog.Content>
        {/* Persistent live region: the wrapper stays mounted so screen readers
            announce the error when it appears — only the content inside is
            conditional, never the live region itself. */}
        <div role="alert" css={styles.errorRegion}>
          {showError && (
            <InlineNotification
              severity="error"
              title={_t("Couldn't generate this message")}
              onDismiss={onDismissError}
              data-testid="ai-message-modal-generate-error"
            >
              {_t('Something went wrong on our end. Your prompt is saved, try again, edit it, or draft manually.')}
            </InlineNotification>
          )}
        </div>
        <div css={styles.body}>
          <Typography2 component="p" variant="subtitleMedium">
            {_t('What and who do you want your message to focus on?')}
          </Typography2>
          {audienceLabel && (
            <ChipGroup variant="delete" aria-label={_t('Selected audience')}>
              <Chip variant="delete" onClick={() => onRemoveAudience?.()} data-testid="ai-message-modal-audience-pill">
                {audienceLabel}
              </Chip>
            </ChipGroup>
          )}
          <TextField
            aria-label={_t('Message goal')}
            multiline
            // Row 5: prompt box opens ~176px tall per Figma. CDS multiline is an
            // auto-sizing textarea (sets height inline via JS), so the height is
            // driven by the visible row count, not a CSS min-height (which fights
            // the autosize measurement). 6 rows ≈ 176px; it still grows with content.
            minRows={6}
            // Row 4: focus the prompt box as soon as the modal opens.
            autoFocus
            value={prompt}
            onChange={(event) => onPromptChange(event.target.value)}
            onFocus={handlePromptFocus}
            placeholder={_t(
              "e.g. Send a re-engagement message to learners who haven't been active in the last 2 weeks"
            )}
            data-testid="ai-message-modal-prompt-input"
          />
          <div css={styles.suggestionRow}>
            {suggestions.map((key, index) => {
              const action = AI_MESSAGE_ACTIONS[key];
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleSuggestionClick(key, index)}
                  css={styles.suggestion}
                  data-testid={`ai-message-modal-suggestion-${key}`}
                >
                  <Typography2 component="span" variant="actionPrimary" color="inherit">
                    {action.prompt}
                  </Typography2>
                </button>
              );
            })}
          </div>
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <div css={styles.actions}>
          <Button
            variant="primary"
            onClick={handleGenerate}
            disabled={!canGenerate}
            icon={<AIGenerateIcon />}
            iconPosition="before"
            css={styles.generateButton}
            data-testid="ai-message-modal-generate"
          >
            {_t('Generate')}
          </Button>
          <Button variant="ghost" onClick={onCancel} data-testid="ai-message-modal-cancel">
            {_t('Cancel')}
          </Button>
          <Button
            variant="ghost"
            onClick={handleDraftManually}
            css={styles.manualLink}
            data-testid="ai-message-modal-draft-manually"
          >
            {_t('Draft a message manually')}
          </Button>
        </div>
      </Dialog.Actions>
    </React.Fragment>
  );
};

export default DefineGoalScreen;
